/**
 * CalendarLogger.js - Enhanced logging utility with essential error tracking
 * ----------------------------------------------------------------------------
 * Enhanced logging utility for the calendar application.
 *
 * Provides structured, prefixed, and timestamped log output with optional debug
 * mode. Used throughout the calendar system for consistent logging of info,
 * warnings, errors, and debugging details. Also tracks timing and groups logs
 * for better observability in development.
 *
 * Responsibilities:
 * - Provide structured logging with timestamps and prefixes
 * - Handle debug mode toggling and log level management
 * - Manage log history and timing operations
 * - Essential error tracking for critical logging failures
 *
 * Key Features:
 * - Toggleable debug mode for verbose output
 * - Support for console.info, warn, error, and debug with timestamp formatting
 * - Console grouping and performance timing (start/stop)
 * - Internal log history with optional retrieval and clearing
 * - Essential error tracking for logging system failures
 *
 * Debug Mode Behavior:
 * - info(), warn(), error() ALWAYS log regardless of debug mode
 * - debug(), group(), groupEnd(), time(), timeEnd() ONLY log when debug=true
 *
 * Key Methods:
 * - info()/warn()/error()/debug() - Logging methods with error handling
 * - time()/timeEnd() - Performance timing with error tracking
 * - getHistory()/clearHistory() - Log management
 * - getRecentErrors() - Recent failure debugging
 * - hasCriticalIssues() - Quick error status check
 * - clearErrorHistory() - Reset error tracking
 *
 * Error Handling:
 * - configuration: Logging setup and state management failures
 * - initialization: Logger creation and setup failures
 *
 * Usage:
 *   const logger = new CalendarLogger(true);
 *   logger.info('Calendar initialized');
 *   logger.time('loadEvents');
 *   // ... later ...
 *   logger.timeEnd('loadEvents');
 *
 * Limitations:
 * - Log history is kept in memory (up to `maxHistorySize`)
 * - Does not support external log transport or persistent storage
 * - Should not be used in production for sensitive data without redaction
 *
 * Note: Ultra-minimal error tracking approach - focuses only on critical failures
 * that could break logging functionality. Avoids circular dependencies.
 */

import ErrorHandler from '../../utils/ErrorHandler.js';

// Essential error tracking setup (ultra-minimal to avoid circular dependencies)
const errorHandler = new ErrorHandler({
  module: 'CalendarLogger',
  maxErrors: 5
});

export default class CalendarLogger {
  constructor(debug = false) {
    try {
      this.debugMode = debug;  // Changed from this.debug to avoid confusion
      this.prefix = '📅 Calendar:';
      this.logLevel = debug ? 'debug' : 'info';
      this.startTimes = new Map();
      this.logHistory = [];
      this.maxHistorySize = 100;

      errorHandler.logSuccess('CalendarLogger initialized successfully', {
        category: 'initialization',
        action: 'logger-init-success',
        debugMode: debug
      });

    } catch (error) {
      errorHandler.handleFatalError('Critical error during CalendarLogger initialization', error, {
        category: 'initialization',
        action: 'fatal-logger-init-error'
      });
      throw error;
    }
  }

  /**
   * Log info message (ALWAYS logs, regardless of debug mode)
   * INFO: Changed from debug-only to always-log per logging best practices
   */
  info(...args) {
    try {
      const message = this._formatMessage('info', args);
      console.log(message);
      this._addToHistory('info', message);
    } catch (error) {
      errorHandler.handleError('Error in info logging', error, {
        category: 'configuration',
        action: 'info-logging-failed'
      });
    }
  }

  /**
   * Log warning message (always shown)
   */
  warn(...args) {
    try {
      const message = this._formatMessage('warn', args);
      console.warn(message);
      this._addToHistory('warn', message);
    } catch (error) {
      errorHandler.handleError('Error in warn logging', error, {
        category: 'configuration',
        action: 'warn-logging-failed'
      });
    }
  }

  /**
   * Log error message (always shown)
   */
  error(...args) {
    try {
      const message = this._formatMessage('error', args);
      console.error(message);
      this._addToHistory('error', message);
    } catch (error) {
      errorHandler.handleError('Error in error logging', error, {
        category: 'configuration',
        action: 'error-logging-failed'
      });
    }
  }

  /**
   * Log debug message (only in debug mode) - ALWAYS EXISTS AS FUNCTION
   * DEBUG: Only gated by debug mode - for verbose/detailed output
   */
  debug(...args) {
    try {
      if (this.debugMode) {
        const message = this._formatMessage('debug', args);
        console.debug(message);
        this._addToHistory('debug', message);
      }
      // Method always exists, just doesn't log when debug=false
    } catch (error) {
      errorHandler.handleError('Error in debug logging', error, {
        category: 'configuration',
        action: 'debug-logging-failed'
      });
    }
  }

  /**
   * Start a console group (only in debug mode)
   * GROUPING: Gated by debug mode - for development organization
   */
  group(label) {
    try {
      if (this.debugMode) {
        const fullLabel = `${this.prefix} ${label}`;
        console.group(fullLabel);
        this._addToHistory('group', fullLabel);
      }
    } catch (error) {
      errorHandler.handleError('Error in group logging', error, {
        category: 'configuration',
        action: 'group-logging-failed'
      });
    }
  }

  /**
   * End a console group (only in debug mode)
   * GROUPING: Gated by debug mode - for development organization
   */
  groupEnd() {
    try {
      if (this.debugMode) {
        console.groupEnd();
        this._addToHistory('groupEnd', 'Group ended');
      }
    } catch (error) {
      errorHandler.handleError('Error in groupEnd logging', error, {
        category: 'configuration',
        action: 'group-end-failed'
      });
    }
  }

  /**
   * Start timing an operation (only in debug mode)
   * TIMING: Gated by debug mode - for performance profiling
   */
  time(label) {
    try {
      if (this.debugMode) {
        const fullLabel = `${this.prefix} ${label}`;
        this.startTimes.set(label, Date.now());
        console.time(fullLabel);
      }
    } catch (error) {
      errorHandler.handleError('Error starting timing operation', error, {
        category: 'configuration',
        action: 'timing-start-failed',
        label: label
      });
    }
  }

  /**
   * End timing an operation (only in debug mode)
   * TIMING: Gated by debug mode - for performance profiling
   */
  timeEnd(label) {
    try {
      if (this.debugMode) {
        const fullLabel = `${this.prefix} ${label}`;
        const startTime = this.startTimes.get(label);

        if (startTime) {
          const duration = Date.now() - startTime;
          console.timeEnd(fullLabel);
          this._addToHistory('timing', `${label}: ${duration}ms`);
          this.startTimes.delete(label);
        }
      }
    } catch (error) {
      errorHandler.handleError('Error ending timing operation', error, {
        category: 'configuration',
        action: 'timing-end-failed',
        label: label
      });
    }
  }

  /**
   * Get log history
   */
  getHistory() {
    try {
      return this.logHistory.slice();
    } catch (error) {
      errorHandler.handleError('Error getting log history', error, {
        category: 'configuration',
        action: 'get-history-failed'
      });
      return [];
    }
  }

  /**
   * Clear log history
   */
  clearHistory() {
    try {
      this.logHistory.length = 0;
    } catch (error) {
      errorHandler.handleError('Error clearing log history', error, {
        category: 'configuration',
        action: 'clear-history-failed'
      });
    }
  }

  /**
   * Get recent logs (last N entries)
   */
  getRecentLogs(count = 10) {
    try {
      return this.logHistory.slice(-count);
    } catch (error) {
      errorHandler.handleError('Error getting recent logs', error, {
        category: 'configuration',
        action: 'get-recent-logs-failed'
      });
      return [];
    }
  }

  /**
   * Format log message with prefix and timestamp
   * IMPROVED: Now handles objects, arrays, and errors meaningfully
   * instead of producing [object Object]
   */
  _formatMessage(level, args) {
    const timestamp = new Date().toISOString().substr(11, 12);
    const formattedArgs = args.map(arg => this._formatArg(arg));
    const message = formattedArgs.join(' ');
    return `${this.prefix} [${timestamp}] ${message}`;
  }

  /**
   * Format a single argument for logging
   * Handles primitives, objects, arrays, and errors appropriately
   * @private
   */
  _formatArg(arg) {
    // Handle null/undefined explicitly
    if (arg === null) return 'null';
    if (arg === undefined) return 'undefined';

    // Handle primitives directly
    if (typeof arg !== 'object') {
      return String(arg);
    }

    // Handle Error objects - extract useful information
    if (arg instanceof Error) {
      const parts = [arg.name || 'Error', arg.message];
      if (arg.cause) {
        parts.push(`(cause: ${this._formatArg(arg.cause)})`);
      }
      return parts.filter(Boolean).join(': ');
    }

    // Handle arrays
    if (Array.isArray(arg)) {
      try {
        return JSON.stringify(arg);
      } catch {
        return `[Array(${arg.length})]`;
      }
    }

    // Handle plain objects
    try {
      // Attempt JSON stringification for objects
      return JSON.stringify(arg);
    } catch {
      // Fallback for circular references or non-serializable objects
      const constructor = arg.constructor?.name || 'Object';
      const keys = Object.keys(arg);
      if (keys.length === 0) {
        return `[${constructor}]`;
      }
      // Show first few keys for debugging context
      const preview = keys.slice(0, 3).join(', ');
      const suffix = keys.length > 3 ? ', ...' : '';
      return `[${constructor}: {${preview}${suffix}}]`;
    }
  }

  /**
   * Add entry to log history
   */
  _addToHistory(level, message) {
    try {
      this.logHistory.push({
        level,
        message,
        timestamp: new Date().toISOString()
      });

      // Keep history size manageable
      if (this.logHistory.length > this.maxHistorySize) {
        this.logHistory.shift();
      }
    } catch (error) {
      errorHandler.handleError('Error adding to log history', error, {
        category: 'configuration',
        action: 'add-history-failed'
      });
    }
  }

  /**
   * Set debug mode
   */
  setDebug(debug) {
    try {
      this.debugMode = debug;
      this.logLevel = debug ? 'debug' : 'info';
    } catch (error) {
      errorHandler.handleError('Error setting debug mode', error, {
        category: 'configuration',
        action: 'set-debug-failed'
      });
    }
  }

  /**
   * Check if debug mode is enabled
   */
  isDebug() {
    return this.debugMode;
  }

  /**
   * Get recent errors for debugging
   * @param {number} minutes - Minutes to look back (default: 5)
   * @returns {Array} Recent error entries
   */
  getRecentErrors(minutes = 5) {
    return errorHandler.getRecentErrors(minutes);
  }

  /**
   * Check if there are critical issues that need attention
   * @returns {boolean} True if critical issues exist
   */
  hasCriticalIssues() {
    return errorHandler.hasCriticalIssues();
  }

  /**
   * Clear error history (useful for testing or after resolving issues)
   */
  clearErrorHistory() {
    errorHandler.clearErrorHistory();
  }

  /**
   * Get comprehensive stats including error information
   * @returns {Object} Stats object with error data
   */
  getStats() {
    const errorStats = errorHandler.getErrorStats();

    return {
      debugMode: this.debugMode,
      logLevel: this.logLevel,
      historySize: this.logHistory.length,
      maxHistorySize: this.maxHistorySize,
      activeTimers: this.startTimes.size,
      errors: {
        total: errorStats.totalErrors,
        fatal: errorStats.fatalErrors,
        recent: errorStats.recentErrors,
        byCategory: errorStats.errorsByCategory
      }
    };
  }
}
